import {
  Box,
  Button,
  Grid,
  GridItem,
  Heading,
  Select,
  useToast,
  Card,
  CardBody,
  VStack,
  IconButton,
  Text,
  Flex,
  FormLabel,
  FormControl,
  SimpleGrid,
  Radio,
  RadioGroup,
  Checkbox,
  Input,
  Textarea,
  Switch,
  HStack,
  List,
  ListItem,
  Spinner,
} from "@chakra-ui/react";
import { useEffect, useState, useRef, useCallback } from "react";
import { fetchAllVehiclesList } from "../../../actions/management.action";
import { getDriversList } from "../../../actions/inventory/driver.action";
import { getVendorsList } from "../../../actions/inventory/vendor.action";
import { fetchSuggestions } from "../../../actions/location.actions"; // same as vendor form
import InputWithDropdown from "../../../components/InputWithDropdown";
import { FaRegCopy } from "react-icons/fa";
import { useFormik } from "formik";
import * as Yup from "yup";
import LabelInput from "../../../components/Input";
import InputAndDragAndDrop from "../../../components/InputWithDragAndDrop";
import { Link } from "react-router-dom";
import states from "../../../constants/states.json"; // same as vendor form

// ─── Validation Helpers ───────────────────────────────────────────────────────

const CURRENT_YEAR = new Date().getFullYear();
const TODAY = new Date();
TODAY.setHours(0, 0, 0, 0);

const REGISTRATION_NUMBER_REGEX =
  /^[A-Z]{2}[-\s]?[0-9]{1,2}[-\s]?[A-Z]{1,3}[-\s]?[0-9]{1,4}$/i;
const LETTERS_ONLY_REGEX = /^[a-zA-Z\s]+$/;
const ALPHANUMERIC_REGEX = /^[a-zA-Z0-9\s\-\,\/]+$/;
const VEHICLE_NUMBER_REGEX = /^[a-zA-Z0-9\-_\/]+$/;

// ─── Yup Validation Schema ────────────────────────────────────────────────────

const buildValidationSchema = () =>
  Yup.object({
    // Step 1 — Vehicle Segment
    vehicleType: Yup.string().required("Vehicle type is required"),

    model: Yup.string()
      .required("Model is required")
      .min(2, "Model must be at least 2 characters")
      .max(50, "Model must not exceed 50 characters")
      .matches(
        ALPHANUMERIC_REGEX,
        "Model should only contain letters, numbers, hyphens or spaces",
      ),

    manufacturer: Yup.string()
      .required("Manufacturer is required")
      .min(2, "Manufacturer must be at least 2 characters")
      .max(50, "Manufacturer must not exceed 50 characters")
      .matches(
        LETTERS_ONLY_REGEX,
        "Manufacturer should only contain letters and spaces",
      ),

    year: Yup.string()
      .required("Year is required")
      .matches(/^\d{4}$/, "Year must be exactly 4 digits")
      .test(
        "min-year",
        "Year must be 1900 or later",
        (val) => !val || parseInt(val) >= 1900,
      )
      .test(
        "max-year",
        `Year cannot be in the future (max ${CURRENT_YEAR})`,
        (val) => !val || parseInt(val) <= CURRENT_YEAR,
      ),

    regEndDate: Yup.date()
      .required("Registration end date is required")
      .typeError("Please enter a valid date")
      .min(TODAY, "Registration end date must be today or a future date"),

    vehicleColor: Yup.string()
      .required("Vehicle color is required")
      .min(2, "Color name must be at least 2 characters")
      .max(50, "Vehicle color must not exceed 50 characters")
      .matches(LETTERS_ONLY_REGEX, "Vehicle color should only contain letters"),

    seatingCapacity: Yup.string()
      .required("Seating capacity is required")
      .matches(/^\d+$/, "Seating capacity must be a number")
      .test(
        "min-seats",
        "Seating capacity must be at least 1",
        (val) => !val || parseInt(val) >= 1,
      )
      .test(
        "max-seats",
        "Seating capacity cannot exceed 100",
        (val) => !val || parseInt(val) <= 100,
      ),

    fuelType: Yup.string()
      .required("Please select a fuel type")
      .oneOf(
        ["electric", "petrol", "diesel", "cng"],
        "Invalid fuel type selected",
      ),

    vehicleNotes: Yup.string().max(500, "Notes must not exceed 500 characters"),

    // NEW: Vehicle location — both optional but validated if provided
    vehicleLocationCity: Yup.string()
      .max(50, "City name must not exceed 50 characters")
      .optional(),

    vehicleLocationState: Yup.string()
      .max(50, "State name must not exceed 50 characters")
      .optional(),

    registrationNumber: Yup.string()
      .required("Registration number is required")
      .matches(
        REGISTRATION_NUMBER_REGEX,
        "Invalid format. Use standard Indian format e.g. MH-02-AB-1234",
      ),

    // Step 2 — Insurance
    insuranceProvider: Yup.string()
      .required("Insurance provider is required")
      .min(2, "Insurance provider must be at least 2 characters")
      .max(50, "Insurance provider must not exceed 50 characters")
      .matches(
        LETTERS_ONLY_REGEX,
        "Insurance provider should only contain letters and spaces",
      ),

    policyNumber: Yup.string()
      .required("Policy number is required")
      .min(10, "Policy number must be at least 10 characters")
      .max(25, "Policy number must not exceed 25 characters")
      .matches(
        /^[A-Z0-9]{2,}[-\/]?[A-Z0-9]+[-\/]?[A-Z0-9]+[-\/]?[A-Z0-9]*$/i,
        "Invalid policy number format. Examples: 3001/123456789/00/000, OG-24-1234-5678-00123",
      ),

    insuranceExpiryDate: Yup.date()
      .required("Insurance expiry date is required")
      .typeError("Please enter a valid date")
      .min(TODAY, "Insurance expiry date must be today or a future date"),

    fitnessEndDate: Yup.date()
      .required("Fitness end date is required")
      .typeError("Please enter a valid date")
      .min(TODAY, "Fitness end date must be today or a future date"),

    pucEndDate: Yup.date()
      .required("PUC end date is required")
      .typeError("Please enter a valid date")
      .min(TODAY, "PUC end date must be today or a future date"),

    permitEndDate: Yup.date()
      .required("Permit end date is required")
      .typeError("Please enter a valid date")
      .min(TODAY, "Permit end date must be today or a future date"),

    taxPaidTillDate: Yup.date()
      .required("Tax paid till date is required")
      .typeError("Please enter a valid date")
      .min(TODAY, "Tax paid till date must be today or a future date"),

    vehicleNumber: Yup.string()
      .required("Vehicle number is required")
      .min(2, "Vehicle number must be at least 2 characters")
      .max(30, "Vehicle number must not exceed 30 characters")
      .matches(
        VEHICLE_NUMBER_REGEX,
        "Vehicle number should only contain letters, numbers, hyphens or underscores",
      ),

    // Step 3 — Owner and Status
    ownerType: Yup.string()
      .required("Owner type is required")
      .oneOf(["driver", "vendor"], "Invalid owner type"),

    ownerId: Yup.string().required("Please search and select an owner"),

    realOwnerName: Yup.string()
      .required("Real owner name is required")
      .min(2, "Name must be at least 2 characters")
      .max(50, "Real owner name must not exceed 50 characters")
      .matches(
        LETTERS_ONLY_REGEX,
        "Owner name should only contain letters and spaces",
      ),

    ownerPhoto: Yup.mixed().notRequired(),

    ownershipAgreement: Yup.mixed().when("someoneElseVehicle", {
      is: true,
      then: (schema) =>
        schema.required("Ownership agreement document is required"),
      otherwise: (schema) => schema.notRequired(),
    }),

    operationalStatus: Yup.string().required("Operational status is required"),
    administrativeStatus: Yup.string().required(
      "Administrative status is required",
    ),
  });

// ─── Inline Error Component ───────────────────────────────────────────────────

const FieldError = ({ error, touched }) =>
  error && touched ? (
    <Text color="red.500" fontSize="xs" mt={1}>
      {error}
    </Text>
  ) : null;

// ─── Toggle Switch Row ────────────────────────────────────────────────────────

const ToggleSwitchField = ({ label, name, value, onChange }) => (
  <FormControl>
    <FormLabel fontSize="1rem" color="#1A202C" mb={3}>
      {label}
    </FormLabel>
    <HStack
      spacing={3}
      h="56px"
      px={4}
      borderRadius="lg"
      bg="gray.100"
      align="center"
    >
      <Text
        fontSize="sm"
        color={!value ? "#1A202C" : "#A0AEC0"}
        fontWeight={!value ? "600" : "400"}
      >
        No
      </Text>
      <Switch
        isChecked={value}
        onChange={(e) => onChange(name, e.target.checked)}
        colorScheme="blue"
        size="md"
      />
      <Text
        fontSize="sm"
        color={value ? "#1A202C" : "#A0AEC0"}
        fontWeight={value ? "600" : "400"}
      >
        Yes
      </Text>
    </HStack>
  </FormControl>
);

// ─── Main Form Component ──────────────────────────────────────────────────────

const Form = ({
  initialValues,
  files,
  setFiles,
  handleSubmit,
  formRef,
  onDraftSave,
}) => {
  const toast = useToast();
  const toastIdRef = useRef({});
  const [vehicleSegmentOptions, setVehicleSegmentOptions] = useState([]);
  const [ownerDetails, setOwnerDetails] = useState(null);

  // ── City autocomplete state (mirrors vendor form pattern) ──
  const [citySuggestions, setCitySuggestions] = useState([]);
  const [loadingCity, setLoadingCity] = useState(false);
  const [showCityDropdown, setShowCityDropdown] = useState(false);
  const debounceTimerRef = useRef(null);

  const showToastOnce = (key, options) => {
    if (!toastIdRef.current[key] || !toast.isActive(toastIdRef.current[key])) {
      toastIdRef.current[key] = toast({ ...options });
    }
  };

  const formik = useFormik({
    initialValues,
    validationSchema: buildValidationSchema(),
    enableReinitialize: true,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        await handleSubmit(values);
      } catch (error) {
        console.error("Submit error:", error);
      } finally {
        setSubmitting(false);
      }
    },
  });

  const {
    values,
    errors,
    touched,
    handleBlur,
    setFieldValue,
    setFieldTouched,
  } = formik;

  // ─── Submit Handler ───────────────────────────────────────────────────────────

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = await formik.validateForm();

    if (Object.keys(validationErrors).length > 0) {
      formik.setTouched(
        Object.keys(validationErrors).reduce(
          (acc, key) => ({ ...acc, [key]: true }),
          {},
        ),
        true,
      );

      const firstKey = Object.keys(validationErrors)[0];
      const firstMsg = validationErrors[firstKey];
      const message = Array.isArray(firstMsg)
        ? firstMsg[0]
        : typeof firstMsg === "string"
          ? firstMsg
          : "Some fields are missing or invalid.";

      showToastOnce("submit-error", {
        title: "Please fix the errors before submitting",
        description: message,
        status: "error",
        duration: 4000,
        isClosable: true,
        position: "top-right",
      });
      return;
    }

    formik.handleSubmit(e);
  };

  // ─── Field Change Handlers ────────────────────────────────────────────────────

  const handleLettersOnlyChange = (e) => {
    const { name, value } = e.target;
    const MAX_50 = [
      "manufacturer",
      "vehicleColor",
      "insuranceProvider",
      "realOwnerName",
    ];
    const stripped = value.replace(/[0-9]/g, "");
    setFieldValue(
      name,
      MAX_50.includes(name) ? stripped.slice(0, 51) : stripped,
    );
    setFieldTouched(name, true, false);
  };

  const handleRegistrationNumberChange = (e) => {
    const { name, value } = e.target;
    setFieldValue(name, value.toUpperCase());
    setFieldTouched(name, true, false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "vehicleType") {
      const selected = vehicleSegmentOptions.find((o) => o._id === value);
      if (selected?.passengerCapacity != null)
        setFieldValue("seatingCapacity", String(selected.passengerCapacity));
      if (selected?.vehicleTypes != null)
        setFieldValue("model", selected.vehicleTypes);
      if (selected?.fuelTypes) {
        const fuel = Array.isArray(selected.fuelTypes)
          ? selected.fuelTypes[0]?.toLowerCase()
          : selected.fuelTypes?.toLowerCase();
        setFieldValue("fuelType", fuel);
      }
    }

    setFieldValue(name, value);
    setFieldTouched(name, true, false);
  };

  const handleToggleChange = (name, checked) => {
    setFieldValue(name, checked);
    setFieldTouched(name, true, false);
  };

  // ─── City Autocomplete (same pattern as vendor form) ─────────────────────────

  const handleCityChange = useCallback(
    (value) => {
      // Strip non-letter characters, same as vendor form
      const clean = value.replace(/[^A-Za-z\s]/g, "");
      setFieldValue("vehicleLocationCity", clean);
      setFieldTouched("vehicleLocationCity", true, false);

      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);

      if (!clean.trim()) {
        setCitySuggestions([]);
        setShowCityDropdown(false);
        return;
      }

      debounceTimerRef.current = setTimeout(async () => {
        setLoadingCity(true);
        setShowCityDropdown(true);
        try {
          const response = await fetchSuggestions(clean, "cities");
          setCitySuggestions(response?.data || []);
        } catch (err) {
          console.error("City fetch error:", err);
        } finally {
          setLoadingCity(false);
        }
      }, 300);
    },
    [setFieldValue, setFieldTouched],
  );

  const handleCitySelect = (location) => {
    setFieldValue("vehicleLocationCity", location.description || location.name);
    setShowCityDropdown(false);
    setCitySuggestions([]);
  };

  // ─── Owner Handlers ───────────────────────────────────────────────────────────

  const findOwnerDetails = async (query) => {
    try {
      if (values.ownerType === "vendor") {
        const response = await getVendorsList(1, 10, query);
        if (!response?.data || !Array.isArray(response.data)) return [];
        return response.data.map((v) => ({
          id: v._id,
          uniqueId: v.uniqueVendorCode || v._id,
          name: v.vendorName || "N/A",
          email: v.contactDetails?.email || "N/A",
        }));
      } else {
        const response = await getDriversList(1, 10, query);
        if (!response?.data || !Array.isArray(response.data)) return [];
        return response.data.map((d) => ({
          id: d._id,
          uniqueId: d.uniqueDriverId || d._id,
          name: d.name || "N/A",
          email: d.contactDetails?.email || "N/A",
        }));
      }
    } catch (error) {
      console.error("Error fetching owner details:", error);
      toast({
        title: "Error",
        description: "Failed to fetch owner details",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
      return [];
    }
  };

  const handleOwnerSelect = (e) => {
    setFieldValue("ownerId", e.target.value);
    setFieldTouched("ownerId", true);
  };

  const handleOwnerDetailsSet = (details) => {
    setOwnerDetails(details);
    if (!values.someoneElseVehicle && details?.name)
      setFieldValue("realOwnerName", details.name);
  };

  const handleOwnerTypeChange = (e) => {
    setFieldValue("ownerType", e.target.value);
    setFieldValue("ownerId", "");
    setOwnerDetails(null);
    if (!values.someoneElseVehicle) setFieldValue("realOwnerName", "");
  };

  const handleSomeoneElseVehicleChange = (e) => {
    const checked = e.target.checked;
    setFieldValue("someoneElseVehicle", checked);
    if (!checked && ownerDetails?.name)
      setFieldValue("realOwnerName", ownerDetails.name);
    else if (checked) setFieldValue("realOwnerName", "");
  };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    showToastOnce("copy", {
      title: "Copied",
      status: "success",
      duration: 2000,
      isClosable: true,
      position: "top-right",
    });
  };

  // ─── Effects ──────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (initialValues?.ownerDetails)
      setOwnerDetails(initialValues.ownerDetails);
  }, [initialValues]);

  useEffect(() => {
    fetchAllVehiclesList()
      .then((res) => setVehicleSegmentOptions(res.data))
      .catch(() =>
        showToastOnce("fetch-segments", {
          title: "Error",
          description: "Unable to fetch Vehicle Segment",
          status: "error",
          duration: 3000,
          isClosable: true,
          position: "top-right",
        }),
      );
  }, []);

  useEffect(() => {
    if (onDraftSave) {
      const timer = setTimeout(() => onDraftSave(values), 1000);
      return () => clearTimeout(timer);
    }
  }, [values, onDraftSave]);

  // ─── Render ───────────────────────────────────────────────────────────────────

  return (
    <Box>
      <form ref={formRef} onSubmit={handleFormSubmit}>
        {/* ══════════════════════════════════════════════════════════
            Step 1: Owner and Status Information
        ══════════════════════════════════════════════════════════ */}
        <Box px={6} py={8} borderRadius="lg" bg="white" my="2rem">
          <Heading fontSize="lg" mb={1} color="#1A202C">
            Owner and Status Information
          </Heading>
          <Flex
            justifyContent="space-between"
            fontSize="0.875rem"
            letterSpacing={0}
            mb={6}
          >
            <Text color="#90A3BF">Please provide owner and status info</Text>
            <Text color="#90A3BF">Step 1 of 3</Text>
          </Flex>

          <Grid
            templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }}
            gap={4}
            mb={6}
          >
            <GridItem>
              <FormLabel fontSize="sm">
                Owner Type{" "}
                <Text as="span" color="red.500">
                  *
                </Text>
              </FormLabel>
              <Select
                placeholder="Select Owner Type"
                name="ownerType"
                value={values.ownerType}
                onChange={handleOwnerTypeChange}
                onBlur={handleBlur}
                variant="outline"
                borderRadius="lg"
                bg={values.ownerType ? "gray.100" : "white"}
                w="100%"
                h="56px"
                isInvalid={!!errors.ownerType && touched.ownerType}
              >
                <option value="driver">Driver</option>
                <option value="vendor">Vendor</option>
              </Select>
              <FieldError
                error={errors.ownerType}
                touched={touched.ownerType}
              />

              <Checkbox
                mt={3}
                isChecked={values.someoneElseVehicle}
                onChange={handleSomeoneElseVehicleChange}
                colorScheme="blue"
                size="md"
              >
                <Text fontSize="sm" color="#4A5568">
                  Someone else's vehicle
                </Text>
              </Checkbox>
            </GridItem>

            <GridItem>
              <FormLabel fontSize="sm">
                Search{" "}
                {values.ownerType === "vendor"
                  ? "Vendor"
                  : values.ownerType === "driver"
                    ? "Driver"
                    : "Owner"}{" "}
                <Text as="span" color="red.500">
                  *
                </Text>
              </FormLabel>
              <InputWithDropdown
                placeholder={`Search ${values.ownerType === "vendor" ? "Vendor" : values.ownerType === "driver" ? "Driver" : "Owner"} (Name, Email, Number)`}
                fetchFunction={findOwnerDetails}
                setSelected={handleOwnerSelect}
                setDetails={handleOwnerDetailsSet}
                name="ownerId"
              />
              {errors.ownerId && touched.ownerId && !ownerDetails && (
                <Text color="red.500" fontSize="xs" mt={1}>
                  {errors.ownerId}
                </Text>
              )}
            </GridItem>

            <GridItem>
              {ownerDetails && (
                <Card
                  mt={4}
                  p={4}
                  borderRadius="md"
                  boxShadow="md"
                  bg="gray.50"
                >
                  <CardBody>
                    <VStack align="start" spacing={2}>
                      <Box display="flex" alignItems="center" gap={2}>
                        <Text fontWeight="bold" fontSize="sm">
                          {values.ownerType === "vendor"
                            ? "Vendor Name"
                            : "Driver Name"}
                          : {ownerDetails.name}
                        </Text>
                        <IconButton
                          aria-label="Copy ID"
                          icon={<FaRegCopy />}
                          size="xs"
                          onClick={() => handleCopy(ownerDetails.uniqueId)}
                        />
                      </Box>
                      <Text fontSize="sm">Email: {ownerDetails.email}</Text>
                      <Text fontSize="sm">ID: {ownerDetails.uniqueId}</Text>
                    </VStack>
                  </CardBody>
                </Card>
              )}
            </GridItem>

            {values.someoneElseVehicle && (
              <GridItem>
                <FormLabel fontSize="sm">
                  Real Owner Name{" "}
                  <Text as="span" color="red.500">
                    *
                  </Text>
                </FormLabel>
                <LabelInput
                  placeholder="Enter real owner name"
                  name="realOwnerName"
                  value={values.realOwnerName}
                  onChange={handleLettersOnlyChange}
                  onBlur={handleBlur}
                  isInvalid={!!errors.realOwnerName && touched.realOwnerName}
                  label={null}
                />
                <FieldError
                  error={errors.realOwnerName}
                  touched={touched.realOwnerName}
                />
              </GridItem>
            )}
          </Grid>

          {/* Owner Photo (always) + Ownership Agreement (someoneElseVehicle only) */}
          <Grid
            templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }}
            gap={4}
            mb={6}
          >
            {/* <GridItem>
              <InputAndDragAndDrop
                title="Upload Owner Photo"
                name="ownerPhoto"
                url={values.ownerPhoto}
                setFiles={setFiles}
                setFieldValue={setFieldValue}
                setFieldTouched={setFieldTouched}
              />
              <FieldError
                error={errors.ownerPhoto}
                touched={touched.ownerPhoto}
              />
            </GridItem> */}

            {values.someoneElseVehicle && (
              <GridItem>
                <InputAndDragAndDrop
                  title="Upload Ownership Agreement *"
                  name="ownershipAgreement"
                  url={values.ownershipAgreement}
                  setFiles={setFiles}
                  setFieldValue={setFieldValue}
                  setFieldTouched={setFieldTouched}
                />
                <FieldError
                  error={errors.ownershipAgreement}
                  touched={touched.ownershipAgreement}
                />
              </GridItem>
            )}
          </Grid>

          {/* Status */}
          <Heading fontSize="md" mb={4}>
            Status
          </Heading>
          <Grid
            templateColumns={{ base: "1fr", md: "repeat(4, 1fr)" }}
            gap={4}
            mb={6}
          >
            <GridItem>
              <FormLabel fontSize="sm">
                Operational Status{" "}
                <Text as="span" color="red.500">
                  *
                </Text>
              </FormLabel>
              <Select
                onChange={handleChange}
                onBlur={handleBlur}
                name="operationalStatus"
                value={values.operationalStatus}
                placeholder="Select Operational Status"
                variant="outline"
                borderRadius="lg"
                bg={values.operationalStatus ? "gray.100" : "white"}
                w="100%"
                h="56px"
                isInvalid={
                  !!errors.operationalStatus && touched.operationalStatus
                }
              >
                <option value="in_review">In Review</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="blacklisted">Blacklisted</option>
              </Select>
              <FieldError
                error={errors.operationalStatus}
                touched={touched.operationalStatus}
              />
            </GridItem>

            <GridItem>
              <FormLabel fontSize="sm">
                Administrative Status{" "}
                <Text as="span" color="red.500">
                  *
                </Text>
              </FormLabel>
              <Select
                onChange={handleChange}
                onBlur={handleBlur}
                name="administrativeStatus"
                value={values.administrativeStatus}
                placeholder="Select Administrative Status"
                variant="outline"
                borderRadius="lg"
                bg={values.administrativeStatus ? "gray.100" : "white"}
                w="100%"
                h="56px"
                isInvalid={
                  !!errors.administrativeStatus && touched.administrativeStatus
                }
              >
                <option value="0">Available</option>
                <option value="1">On Duty</option>
                <option value="2">Under Maintenance</option>
                <option value="3">Out Of Service</option>
              </Select>
              <FieldError
                error={errors.administrativeStatus}
                touched={touched.administrativeStatus}
              />
            </GridItem>
          </Grid>
        </Box>

        {/* ══════════════════════════════════════════════════════════
            Step 2: Vehicle Segment Information
        ══════════════════════════════════════════════════════════ */}
        <Box px={6} py={8} borderRadius="lg" bg="white" my="2rem">
          <Heading fontSize="lg" mb={1} color="#1A202C">
            Vehicle Segment Information
          </Heading>
          <Flex
            justifyContent="space-between"
            fontSize="0.875rem"
            letterSpacing={0}
            mb={6}
          >
            <Text color="#90A3BF">Please provide basic info</Text>
            <Text color="#90A3BF">Step 2 of 3</Text>
          </Flex>

          {/* Row 1: Type / Model / Manufacturer / Year */}
          <Grid
            templateColumns={{
              base: "1fr",
              md: "repeat(2, 1fr)",
              lg: "repeat(4, 1fr)",
            }}
            gap={4}
            mb={6}
          >
            <GridItem>
              <FormLabel fontSize="sm" htmlFor="vehicleType">
                Select Vehicle Type{" "}
                <Text as="span" color="red.500">
                  *
                </Text>
              </FormLabel>
              <Select
                id="vehicleType"
                name="vehicleType"
                value={values.vehicleType}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="Select Vehicle Type"
                variant="filled"
                borderRadius="lg"
                w="100%"
                h="56px"
                isInvalid={!!errors.vehicleType && touched.vehicleType}
              >
                {vehicleSegmentOptions?.map((o) => (
                  <option key={o._id} value={o._id}>
                    {o.segmentName}
                  </option>
                ))}
              </Select>
              <FieldError
                error={errors.vehicleType}
                touched={touched.vehicleType}
              />
            </GridItem>

            <GridItem>
              <LabelInput
                name="model"
                value={values.model}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="e.g., XUV 700, Swift, City"
                label="Model"
                isRequired
                isInvalid={!!errors.model && touched.model}
              />
              <FieldError error={errors.model} touched={touched.model} />
            </GridItem>

            <GridItem>
              <LabelInput
                placeholder="e.g., Mahindra, Maruti, Honda"
                label="Manufacturer"
                name="manufacturer"
                value={values.manufacturer}
                onChange={handleLettersOnlyChange}
                onBlur={handleBlur}
                isRequired
                isInvalid={!!errors.manufacturer && touched.manufacturer}
              />
              <FieldError
                error={errors.manufacturer}
                touched={touched.manufacturer}
              />
            </GridItem>

            <GridItem>
              <FormControl isRequired>
                <FormLabel fontSize="1rem" color="#1A202C">
                  Year
                </FormLabel>
                <Input
                  placeholder={`e.g., ${CURRENT_YEAR}`}
                  name="year"
                  value={values.year}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (/^\d{0,4}$/.test(val)) {
                      setFieldValue("year", val);
                      setFieldTouched("year", true, false);
                    }
                  }}
                  onBlur={handleBlur}
                  type="text"
                  inputMode="numeric"
                  maxLength={4}
                  variant="filled"
                  borderRadius="lg"
                  h="56px"
                  isInvalid={!!errors.year && touched.year}
                />
              </FormControl>
              <FieldError error={errors.year} touched={touched.year} />
            </GridItem>
          </Grid>

          {/* Row 2: Reg End Date / Color / Seating / Fuel Type */}
          <Grid
            templateColumns={{
              base: "1fr",
              md: "repeat(2, 1fr)",
              lg: "repeat(4, 1fr)",
            }}
            gap={4}
            mb={6}
          >
            <GridItem>
              <LabelInput
                placeholder="e.g., 2025-12-31"
                label="Registration End Date"
                name="regEndDate"
                value={values.regEndDate}
                onChange={handleChange}
                onBlur={handleBlur}
                isRequired
                type="date"
                isInvalid={!!errors.regEndDate && touched.regEndDate}
              />
              <FieldError
                error={errors.regEndDate}
                touched={touched.regEndDate}
              />
            </GridItem>

            <GridItem>
              <LabelInput
                placeholder="e.g., White, Black, Silver"
                label="Vehicle Color"
                name="vehicleColor"
                value={values.vehicleColor}
                onChange={handleLettersOnlyChange}
                onBlur={handleBlur}
                isRequired
                isInvalid={!!errors.vehicleColor && touched.vehicleColor}
              />
              <FieldError
                error={errors.vehicleColor}
                touched={touched.vehicleColor}
              />
            </GridItem>

            <GridItem>
              <FormControl isRequired>
                <FormLabel fontSize="1rem" color="#1A202C">
                  Seating Capacity
                </FormLabel>
                <Input
                  placeholder="e.g., 4, 5, 7"
                  name="seatingCapacity"
                  value={values.seatingCapacity}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (/^\d{0,3}$/.test(val)) {
                      setFieldValue("seatingCapacity", val);
                      setFieldTouched("seatingCapacity", true, false);
                    }
                  }}
                  onBlur={handleBlur}
                  type="text"
                  inputMode="numeric"
                  maxLength={3}
                  variant="filled"
                  borderRadius="lg"
                  h="56px"
                  isInvalid={
                    !!errors.seatingCapacity && touched.seatingCapacity
                  }
                />
              </FormControl>
              <FieldError
                error={errors.seatingCapacity}
                touched={touched.seatingCapacity}
              />
            </GridItem>

            <GridItem>
              <FormControl isRequired>
                <FormLabel fontSize="1rem" color="#1A202C">
                  Fuel Type
                </FormLabel>
                <RadioGroup
                  name="fuelType"
                  value={values.fuelType}
                  onChange={(value) => {
                    setFieldValue("fuelType", value);
                    setFieldTouched("fuelType", true);
                  }}
                >
                  <SimpleGrid columns={2} spacing={2}>
                    {["electric", "petrol", "diesel", "cng"].map((type) => (
                      <Radio
                        key={type}
                        value={type}
                        sx={{
                          ".chakra-radio__control": {
                            borderRadius: "4px",
                            width: "20px",
                            height: "20px",
                            borderColor: "#CBD5E0",
                          },
                          ".chakra-radio__control[data-checked]": {
                            backgroundColor: "#3182CE",
                            borderColor: "#3182CE",
                          },
                        }}
                      >
                        {type.charAt(0).toUpperCase() + type.slice(1)}
                      </Radio>
                    ))}
                  </SimpleGrid>
                </RadioGroup>
                {errors.fuelType && touched.fuelType && !values.fuelType && (
                  <Text color="red.500" fontSize="xs" mt={1}>
                    {errors.fuelType}
                  </Text>
                )}
              </FormControl>
            </GridItem>
          </Grid>

          {/* Row 3: GPS / Roof Carrier / Vehicle Notes */}
          <Grid
            templateColumns={{
              base: "1fr",
              md: "repeat(2, 1fr)",
              lg: "repeat(4, 1fr)",
            }}
            gap={4}
            mb={6}
          >
            <GridItem>
              <ToggleSwitchField
                label="GPS Tracking Device"
                name="gpsTracking"
                value={!!values.gpsTracking}
                onChange={handleToggleChange}
              />
            </GridItem>

            <GridItem>
              <ToggleSwitchField
                label="Roof Carrier"
                name="roofCarrier"
                value={!!values.roofCarrier}
                onChange={handleToggleChange}
              />
            </GridItem>

            <GridItem colSpan={{ base: 1, md: 1 }}>
              <FormControl>
                <FormLabel fontSize="1rem" color="#1A202C">
                  Vehicle Notes
                </FormLabel>
                <Textarea
                  name="vehicleNotes"
                  value={values.vehicleNotes}
                  onChange={(e) => {
                    setFieldValue("vehicleNotes", e.target.value);
                    setFieldTouched("vehicleNotes", true, false);
                  }}
                  onBlur={handleBlur}
                  variant="filled"
                  borderRadius="lg"
                  resize="vertical"
                  minH="56px"
                  maxLength={500}
                  isInvalid={!!errors.vehicleNotes && touched.vehicleNotes}
                />
                <Flex justifyContent="space-between" mt={1}>
                  <FieldError
                    error={errors.vehicleNotes}
                    touched={touched.vehicleNotes}
                  />
                  <Text fontSize="xs" color="#90A3BF" ml="auto">
                    {(values.vehicleNotes || "").length}/500
                  </Text>
                </Flex>
              </FormControl>
            </GridItem>

            {/* City */}
            <GridItem>
              <Box position="relative">
                <FormLabel fontSize="1rem" color="#1A202C">
                  City
                </FormLabel>
                <Input
                  placeholder="Search city..."
                  name="vehicleLocationCity"
                  value={values.vehicleLocationCity || ""}
                  onChange={(e) => handleCityChange(e.target.value)}
                  onFocus={() => {
                    if (citySuggestions.length > 0) setShowCityDropdown(true);
                  }}
                  onBlur={() => {
                    handleBlur({ target: { name: "vehicleLocationCity" } });
                    // Delay hide so onMouseDown on list item fires first
                    setTimeout(() => setShowCityDropdown(false), 200);
                  }}
                  autoComplete="off"
                  variant="filled"
                  borderRadius="lg"
                  h="56px"
                  bg={values.vehicleLocationCity ? "gray.100" : undefined}
                  isInvalid={
                    !!errors.vehicleLocationCity && touched.vehicleLocationCity
                  }
                />
                {loadingCity && (
                  <Spinner
                    size="sm"
                    position="absolute"
                    right="12px"
                    top="50%"
                    transform="translateY(20%)"
                    color="blue.400"
                  />
                )}
                {showCityDropdown && citySuggestions.length > 0 && (
                  <List
                    position="absolute"
                    zIndex={10}
                    bg="white"
                    border="1px solid"
                    borderColor="gray.200"
                    borderRadius="lg"
                    width="100%"
                    maxHeight="240px"
                    overflowY="auto"
                    mt={1}
                    boxShadow="lg"
                  >
                    {citySuggestions.map((location, index) => (
                      <ListItem
                        key={index}
                        px={4}
                        py={2.5}
                        cursor="pointer"
                        fontSize="sm"
                        _hover={{ bg: "blue.50" }}
                        onMouseDown={() => handleCitySelect(location)}
                      >
                        {location.description || location.name}
                      </ListItem>
                    ))}
                  </List>
                )}
                <FieldError
                  error={errors.vehicleLocationCity}
                  touched={touched.vehicleLocationCity}
                />
              </Box>
            </GridItem>
          </Grid>

          {/* Row 5: Registration Number + uploads */}
          <Grid
            templateColumns={{
              base: "1fr",
              md: "repeat(2, 1fr)",
              lg: "repeat(4, 1fr)",
            }}
            gap={4}
            mb={6}
          >
            <GridItem colSpan={1}>
              <LabelInput
                placeholder="e.g., MH-02-AB-1234"
                label="Registration Number"
                name="registrationNumber"
                value={values.registrationNumber}
                onChange={handleRegistrationNumberChange}
                onBlur={handleBlur}
                isRequired
                isInvalid={
                  !!errors.registrationNumber && touched.registrationNumber
                }
              />
              <FieldError
                error={errors.registrationNumber}
                touched={touched.registrationNumber}
              />
              {!errors.registrationNumber && (
                <Text fontSize="xs" color="#90A3BF" mt={1}>
                  Format: MH-02-AB-1234
                </Text>
              )}
            </GridItem>
            <GridItem colSpan={3} />

            <GridItem colSpan={{ base: 4, md: 2 }}>
              <InputAndDragAndDrop
                title="Upload Registration Front"
                name="registrationFront"
                url={values.registrationFront}
                setFiles={setFiles}
                setFieldValue={setFieldValue}
                setFieldTouched={setFieldTouched}
              />
            </GridItem>
            <GridItem colSpan={{ base: 4, md: 2 }}>
              <InputAndDragAndDrop
                title="Upload Registration Back"
                name="registrationBack"
                url={values.registrationBack}
                setFiles={setFiles}
                setFieldValue={setFieldValue}
                setFieldTouched={setFieldTouched}
              />
            </GridItem>
          </Grid>
        </Box>

        {/* ══════════════════════════════════════════════════════════
            Step 3: Insurance Details
        ══════════════════════════════════════════════════════════ */}
        <Box px={6} py={8} borderRadius="lg" bg="white" my="2rem">
          <Heading fontSize="lg" mb={1} color="#1A202C">
            Insurance Details
          </Heading>
          <Flex
            justifyContent="space-between"
            fontSize="0.875rem"
            letterSpacing={0}
            mb={6}
          >
            <Text color="#90A3BF">Please provide insurance info</Text>
            <Text color="#90A3BF">Step 3 of 3</Text>
          </Flex>

          <Grid
            templateColumns={{ base: "1fr", md: "repeat(4, 1fr)" }}
            gap={4}
            mb={6}
          >
            <GridItem>
              <LabelInput
                placeholder="e.g., ICICI Lombard, HDFC Ergo"
                label="Insurance Provider"
                name="insuranceProvider"
                value={values.insuranceProvider}
                onChange={handleLettersOnlyChange}
                onBlur={handleBlur}
                isRequired
                bg={values.insuranceProvider ? "gray.100" : "white"}
                isInvalid={
                  !!errors.insuranceProvider && touched.insuranceProvider
                }
              />
              <FieldError
                error={errors.insuranceProvider}
                touched={touched.insuranceProvider}
              />
            </GridItem>

            <GridItem>
              <LabelInput
                placeholder="e.g., POL123456789"
                label="Policy Number"
                name="policyNumber"
                value={values.policyNumber}
                onChange={handleChange}
                onBlur={handleBlur}
                isRequired
                bg={values.policyNumber ? "gray.100" : "white"}
                isInvalid={!!errors.policyNumber && touched.policyNumber}
              />
              <FieldError
                error={errors.policyNumber}
                touched={touched.policyNumber}
              />
              {!errors.policyNumber && (
                <Text fontSize="xs" color="#90A3BF" mt={1}>
                  Format: 3001/123456789/00/000 or OG-24-1234-5678-00123
                </Text>
              )}
            </GridItem>

            <GridItem>
              <LabelInput
                placeholder="e.g., 2025-12-31"
                label="Insurance Expiry Date"
                name="insuranceExpiryDate"
                value={values.insuranceExpiryDate}
                onChange={handleChange}
                onBlur={handleBlur}
                type="date"
                isRequired
                bg={values.insuranceExpiryDate ? "gray.100" : "white"}
                isInvalid={
                  !!errors.insuranceExpiryDate && touched.insuranceExpiryDate
                }
              />
              <FieldError
                error={errors.insuranceExpiryDate}
                touched={touched.insuranceExpiryDate}
              />
            </GridItem>

            <GridItem>
              <LabelInput
                placeholder="e.g., 2026-06-30"
                label="Fitness End Date"
                name="fitnessEndDate"
                value={values.fitnessEndDate}
                onChange={handleChange}
                onBlur={handleBlur}
                type="date"
                isRequired
                bg={values.fitnessEndDate ? "gray.100" : "white"}
                isInvalid={!!errors.fitnessEndDate && touched.fitnessEndDate}
              />
              <FieldError
                error={errors.fitnessEndDate}
                touched={touched.fitnessEndDate}
              />
            </GridItem>
          </Grid>

          <Grid
            templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }}
            gap={4}
            mb={6}
          >
            <GridItem>
              <InputAndDragAndDrop
                title="Upload Insurance Certificate"
                name="insuranceImage"
                url={values.insuranceImage}
                setFiles={setFiles}
                setFieldValue={setFieldValue}
                setFieldTouched={setFieldTouched}
              />
            </GridItem>
            <GridItem>
              <InputAndDragAndDrop
                title="Upload Fitness Certificate"
                name="fitnessCertificate"
                url={values.fitnessCertificate}
                setFiles={setFiles}
                setFieldValue={setFieldValue}
                setFieldTouched={setFieldTouched}
              />
            </GridItem>
          </Grid>

          <Grid
            templateColumns={{ base: "1fr", md: "repeat(4, 1fr)" }}
            gap={4}
            mb={6}
          >
            <GridItem>
              <LabelInput
                placeholder="e.g., 2025-03-31"
                label="PUC End Date"
                name="pucEndDate"
                value={values.pucEndDate}
                onChange={handleChange}
                onBlur={handleBlur}
                type="date"
                isRequired
                bg={values.pucEndDate ? "gray.100" : "white"}
                isInvalid={!!errors.pucEndDate && touched.pucEndDate}
              />
              <FieldError
                error={errors.pucEndDate}
                touched={touched.pucEndDate}
              />
            </GridItem>

            <GridItem>
              <LabelInput
                placeholder="e.g., 2025-06-30"
                label="Permit End Date"
                name="permitEndDate"
                value={values.permitEndDate}
                onChange={handleChange}
                onBlur={handleBlur}
                type="date"
                isRequired
                bg={values.permitEndDate ? "gray.100" : "white"}
                isInvalid={!!errors.permitEndDate && touched.permitEndDate}
              />
              <FieldError
                error={errors.permitEndDate}
                touched={touched.permitEndDate}
              />
            </GridItem>

            <GridItem>
              <LabelInput
                placeholder="e.g., 2025-12-31"
                label="Tax Paid Till Date"
                name="taxPaidTillDate"
                value={values.taxPaidTillDate}
                onChange={handleChange}
                onBlur={handleBlur}
                type="date"
                isRequired
                bg={values.taxPaidTillDate ? "gray.100" : "white"}
                isInvalid={!!errors.taxPaidTillDate && touched.taxPaidTillDate}
              />
              <FieldError
                error={errors.taxPaidTillDate}
                touched={touched.taxPaidTillDate}
              />
            </GridItem>

            <GridItem>
              <LabelInput
                placeholder="e.g., VH-001 or FLEET-2023-001"
                label="Vehicle Number"
                name="vehicleNumber"
                value={values.vehicleNumber}
                onChange={handleChange}
                onBlur={handleBlur}
                isRequired
                bg={values.vehicleNumber ? "gray.100" : "white"}
                isInvalid={!!errors.vehicleNumber && touched.vehicleNumber}
              />
              <FieldError
                error={errors.vehicleNumber}
                touched={touched.vehicleNumber}
              />
            </GridItem>
          </Grid>

          <Grid
            templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }}
            gap={4}
            mb={6}
          >
            <GridItem>
              <InputAndDragAndDrop
                title="Upload PUC Certificate"
                name="pucCertificate"
                url={values.pucCertificate}
                setFiles={setFiles}
                setFieldValue={setFieldValue}
                setFieldTouched={setFieldTouched}
              />
            </GridItem>
            <GridItem>
              <InputAndDragAndDrop
                title="Upload Permit Certificate"
                name="permitCertificate"
                url={values.permitCertificate}
                setFiles={setFiles}
                setFieldValue={setFieldValue}
                setFieldTouched={setFieldTouched}
              />
            </GridItem>
            <GridItem>
              <InputAndDragAndDrop
                title="Upload Tax Document"
                name="taxDocument"
                url={values.taxDocument}
                setFiles={setFiles}
                setFieldValue={setFieldValue}
                setFieldTouched={setFieldTouched}
              />
            </GridItem>
            <GridItem>
              <InputAndDragAndDrop
                title="Upload Vehicle Image"
                name="vehicleImage"
                url={values.vehicleImage}
                setFiles={setFiles}
                setFieldValue={setFieldValue}
                setFieldTouched={setFieldTouched}
              />
            </GridItem>
          </Grid>
          <Flex justifyContent="flex-end" gap={4} mt={6}>
            <Link to="/fleet-management/vehicles">
              <Button
                type="button"
                color="#396AFF"
                variant="outline"
                borderRadius="md"
                border="2px solid #396AFF"
                px={10}
                _hover={{ bg: "blue.50" }}
              >
                Cancel
              </Button>
            </Link>
            <Button
              type="submit"
              bg="#396AFF"
              borderRadius="md"
              px={10}
              color="white"
              _hover={{ bg: "blue.600" }}
              isLoading={formik.isSubmitting}
              loadingText="Submitting..."
            >
              Submit
            </Button>
          </Flex>
        </Box>
      </form>
    </Box>
  );
};

export default Form;
